// ProductsList.jsx — proc-v3 (F2.3d): SECONDA VISTA DEL MONOLITE in React,
// lista "Prodotti", flag-gated (shell_proc_v3_products, default OFF).
//
// Replica 1:1 renderProducts di proc-v0.js:7675-7775: stesso titolo/sottotitolo,
// stessi controlli foto (toggle "Mostra foto" + slider dimensione, STESSE
// chiavi localStorage crm_prod_show_photos/crm_prod_photo_size: v1↔v3 si
// scambiano le preferenze), stesso bottone "+ Nuovo prodotto", stesse colonne
// (Foto condizionale, Nome+KIT, Categoria, Marca, Fornitori che lo offrono,
// Ult. quotazione, Schema), stesso ordinamento di default (_lastQuoteAt desc,
// non-quotati in coda), stesse azioni bulk (Sposta in categoria… / Elimina,
// stesse primitive v1 ⇒ payload identici), stesso click riga → dettaglio V1.
// La tabella è il componente condiviso client/src/ui/DataTable.jsx (nessuna
// prop nuova rispetto a F2.3c: filterable+selectable bastano).
//
// CONFINI (pattern f23c): dettaglio prodotto, modale nuovo prodotto, picker
// categoria = V1 (deleghe in api.js); MAI 'nav:badge' da questo modulo.
//
// STATO:
//  - selezione a livello di PAGINA (factory) con pruning sugli id morti.
//    DEVIAZIONE DICHIARATA rispetto al v1: renderProducts ricrea la tabella
//    (e azzera la selezione) a OGNI redraw — qui la selezione sopravvive ai
//    refresh dati (root React persistente) e si azzera uscendo dalla vista o
//    a bulk conclusa. Mai peggiore del v1 (stesso rito della deviazione 3 f23c).
//  - preferenze foto: React state + write-through sulle chiavi v1.
//    DEVIAZIONE DICHIARATA: il v1 persiste la dimensione solo al RILASCIO
//    dello slider (onchange), qui a ogni variazione — stesso valore finale.
//  - NESSUN focus automatico: showView v1 (proc-v0.js:16476) lo fa solo per
//    suppliers/categories, non per products.
//
// Il chrome usa le stesse classi Tailwind del v1 (il componente vive nel
// documento del monolite, #products-content) — parità visiva senza fork.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  tProd, PROD_TEXTS, readProducts, readProductPrefs, writeShowPhotos, writePhotoSize,
  prodPhoto, fmtQuoteTs, photoFilterText, prodNameFilterText, lastQuoteFilterText,
  openNewProduct, openProductDetail, resolveProductSelection, buildProductBulkActions,
} from './api.js';

export function ProductsList({ win, tick, selection }) {
  const [prefs, setPrefs] = React.useState(() => readProductPrefs(win));
  const [, force] = React.useReducer((n) => n + 1, 0);

  // dati: letti dallo store del monolite a ogni refresh (tick dal wrapper) —
  // stessa composizione del v1 (api.readProducts, quirk d'ordine compreso)
  const data = React.useMemo(() => readProducts(win), [win, tick]);

  // selezione: PRUNA gli id che non esistono più nello store (pattern f23c —
  // il live-sync può cancellare record mentre la vista è aperta)
  React.useEffect(() => {
    let changed = false;
    for (const id of Array.from(selection)) {
      if (!win.store.getProduct(id)) { selection.delete(id); changed = true; }
    }
    if (changed) force();
  }, [data, selection, win]);

  const t = (k) => tProd(win, k);
  const size = prefs.photoSize;

  // colonna Foto (v1:7714-7720): presente SOLO con "Mostra foto" attivo
  const photoCol = {
    key: '_photoCount',
    label: PROD_TEXTS.photoCol,
    sortable: true,
    sortValue: (r) => r._v1Rank._photoCount, // rank v1 (type number): pari merito identici al v1 anche al click
    filterText: photoFilterText,                  // il v1 filtra il testo del badge conteggio
    render: (r, ctx) => {
      const img = prodPhoto(r);
      return (
        <div className="relative inline-block">
          {img ? (
            <img
              src={img.dataUrl}
              className="prod-photo-thumb"
              style={{ width: size, height: size, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
              title={img.name || 'foto'}
              alt=""
            />
          ) : (
            <div
              className="prod-photo-thumb"
              style={{ width: size, height: size, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: Math.round(size * 0.4) }}
            />
          )}
          {r._photoCount > 1 && (
            <span className="absolute -bottom-1 -right-1 bg-slate-900 text-white text-[9px] leading-none px-1 py-0.5 rounded-full">
              <ctx.Mark text={String(r._photoCount)} tokens={ctx.tokens} />
            </span>
          )}
        </div>
      );
    },
  };

  const columns = [
    ...(prefs.showPhotos ? [photoCol] : []),
    {
      key: 'name',
      label: t('col.name'),
      sortable: true,
      sortValue: (r) => r._v1Rank.name, // rank v1 (stringa, confronto `<` su lowercase)
      filterText: prodNameFilterText,
      render: (r, ctx) => (
        <>
          <span className="font-medium"><ctx.Mark text={r.name} tokens={ctx.tokens} /></span>
          {(r.attrs && r.attrs.isKit) ? (
            <>
              {' '}
              <span className="bg-cyan-100 text-cyan-800 text-[9px] px-1 py-0.5 rounded align-middle">
                <ctx.Mark text="KIT" tokens={ctx.tokens} />
              </span>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: 'category',
      label: t('col.category'),
      sortable: true,
      sortValue: (r) => r._v1Rank.category, // rank v1
      render: (r, ctx) => (
        <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs"><ctx.Mark text={String(r.category == null ? '' : r.category)} tokens={ctx.tokens} /></code>
      ),
    },
    {
      key: '_brand',
      label: t('col.brand'),
      sortable: true,
      sortValue: (r) => r._v1Rank._brand, // rank v1
      filterText: (r) => r._brand || '—',
      render: (r, ctx) => <ctx.Mark text={r._brand || '—'} tokens={ctx.tokens} />,
    },
    {
      key: '_supCount',
      label: t('col.suppliers_offering'),
      sortable: true,
      sortValue: (r) => r._v1Rank._supCount, // rank v1 (type number)
    },
    {
      key: '_lastQuoteAt',
      label: PROD_TEXTS.lastQuoteCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._lastQuoteAt, // rank v1 (sortKey stringa: 0 = mai quotato, in coda col desc)
      filterText: lastQuoteFilterText,
      render: (r, ctx) => (r._lastQuoteAt
        ? <span className="text-xs"><ctx.Mark text={fmtQuoteTs(r._lastQuoteAt)} tokens={ctx.tokens} /></span>
        : <span className="text-slate-300"><ctx.Mark text="—" tokens={ctx.tokens} /></span>),
    },
    {
      key: 'schemaVersion',
      label: t('col.schema'),
      sortable: true,
      sortValue: (r) => r._v1Rank.schemaVersion, // rank v1 (type number)
      filterText: (r) => 'v' + String(r.schemaVersion),
      render: (r, ctx) => <span className="text-xs"><ctx.Mark text={'v' + String(r.schemaVersion)} tokens={ctx.tokens} /></span>,
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t('prod.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t('prod.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-slate-600 select-none cursor-pointer">
            <input
              type="checkbox"
              id="prod-show-photos"
              checked={prefs.showPhotos}
              onChange={(e) => {
                const v = e.target.checked;
                writeShowPhotos(win, v);
                setPrefs((p) => ({ ...p, showPhotos: v }));
              }}
            /> {PROD_TEXTS.showPhotos}
          </label>
          <div id="prod-photo-size-wrap" className={'flex items-center gap-1.5 text-xs text-slate-500 ' + (prefs.showPhotos ? '' : 'hidden')}>
            <span>{PROD_TEXTS.photoSizeLabel}</span>
            <input
              id="prod-photo-size"
              type="range"
              min="28"
              max="140"
              step="4"
              value={size}
              className="w-28"
              onChange={(e) => {
                const px = parseInt(e.target.value, 10) || 44;
                writePhotoSize(win, px); // deviazione dichiarata: il v1 persiste solo al rilascio
                setPrefs((p) => ({ ...p, photoSize: px }));
              }}
            />
            <span id="prod-photo-size-val" className="w-9 text-right tabular-nums">{size}px</span>
          </div>
          <button
            id="btn-new-product"
            type="button"
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-sm"
            onClick={() => openNewProduct(win)}
          >{t('prod.new')}</button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3" id="prod-table-mount">
        <DataTable
          columns={columns}
          rows={data.rows}
          rowKey="id"
          sort={{ key: '_lastQuoteAt', dir: 'desc' }}
          filterable
          selectable
          selection={selection}
          resolveSelection={(ids) => resolveProductSelection(win, ids)}
          bulkActions={buildProductBulkActions(win)}
          onBulkDone={() => { selection.clear(); force(); }}
          onRowClick={(row) => openProductDetail(win, row.id)}
          emptyMessage={PROD_TEXTS.emptyMessage}
        />
      </div>
    </div>
  );
}

// Factory (pattern createProcV3Suppliers): il register/install la aggancia al
// WRAP di window.renderProducts. render() idempotente sul container; unmount()
// libera il container (il monolite resta vivo per le altre viste).
export function createProcV3Products(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  // selezione a livello di PAGINA (deviazione dichiarata: il v1 la azzera a
  // ogni renderProducts; qui sopravvive ai refresh, pruning sugli id morti)
  const selection = new Set();

  function render() {
    const container = win.document.getElementById('products-content');
    if (!container) throw new Error('#products-content assente');
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      container.replaceChildren(); // eventuali resti del v1 (catch-up)
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (rerenderAll, bulk, rientro)
    root.render(<ProductsList win={win} tick={tick} selection={selection} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
